//#region Imports Packages and Libs

import {
  IDynamicButtonConfig,
  IDynamicButtonItem,
  IDynamicButtonMap,
  IComponentStyle,
  IStyleComponentItem,
  MessageStructureComponent,
  IElementButton,
} from "../../types";
import { MESSAGE_COMPONENT_TYPE } from "../../enums";
import { Util } from "../../util";
import { getUniqId } from "../get_uniq_id";
import { createLogger } from "../logger";

//#endregion

/**
 * Dynamic Button Manager Helper Module
 *
 * This module provides reusable helper functions for managing dynamic button operations
 * across Carousel components in the application. It handles button validation,
 * transformation, styling, and rendering with error handling and fallback support.
 *
 * @file dynamic_button_manager.ts
 * @author Palash
 * @version 1.0.0
 */

//#region Logger Setup

/**
 * Centralized logger instance for this module
 */
const buttonLogger = createLogger({ context: "dynamic_button_manager.ts" });

//#endregion

//#region Button Validation Functions

/**
 * Validates if the provided object is a valid buttons container.
 *
 * @param obj - Object to validate
 * @returns True if object is valid and contains buttons
 */
export function isValidButtonsObject(obj?: IDynamicButtonMap | null): boolean {
  try {
    return !!obj && typeof obj === "object" && !Array.isArray(obj) && Object.keys(obj).length > 0;
  } catch (error) {
    buttonLogger.error("isValidButtonsObject", error);
    return false;
  }
}

/**
 * Validates if the provided button data is valid for rendering.
 *
 * @param key - The button key
 * @param buttonData - Button data to validate
 * @returns True if button data is valid
 */
export function isValidButtonData(key: string, buttonData: IElementButton) {
  try {
    if (!buttonData || typeof buttonData !== "object" || Array.isArray(buttonData)) {
      return false;
    }

    // Check if buttonData has a valid label
    return (
      Object.keys(buttonData).length > 0 &&
      typeof buttonData.label === "string" &&
      !Util.isNullOrEmpty(buttonData.label)
    );
  } catch (error) {
    buttonLogger.error("isValidButtonData", error);
    return false;
  }
}

/**
 * Validates and returns the buttons object from component if it exists and is valid.
 *
 * @param config - Configuration object containing component data
 * @returns The buttons object or null if invalid
 */
export function getValidButtonsObject(config: IDynamicButtonConfig): IDynamicButtonMap | null {
  try {
    const buttonsObj = config.component?.buttons;

    if (!isValidButtonsObject(buttonsObj)) {
      return null;
    }

    return buttonsObj;
  } catch (error) {
    buttonLogger.error("getValidButtonsObject", error);
    return null;
  }
}

//#endregion

//#region Button Transformation Pipeline

/**
 * Step 1: Validates the input buttons object
 */
function validateButtonsInput(buttonsObj: IDynamicButtonMap): boolean {
  if (!buttonsObj || typeof buttonsObj !== "object" || Array.isArray(buttonsObj)) {
    buttonLogger.warn("validateButtonsInput", "Invalid buttons object provided", buttonsObj);
    return false;
  }
  return true;
}

/**
 * Step 2: Extracts entries from the buttons object
 */
function extractButtonEntries(buttonsObj: IDynamicButtonMap): [string, IElementButton][] {
  const buttonEntries = Object.entries(buttonsObj);
  if (!buttonEntries || buttonEntries.length === 0) {
    buttonLogger.warn("extractButtonEntries", "Empty buttons object provided");
    return [];
  }
  return buttonEntries;
}

/**
 * Step 3: Filters valid button entries
 */
function filterValidEntries(buttonEntries: [string, IElementButton][]): [string, IElementButton][] {
  const validEntries = buttonEntries.filter(([key, buttonData]) => {
    try {
      return isValidButtonData(key, buttonData);
    } catch {
      return false; // Exclude problematic entries
    }
  });

  if (!validEntries || validEntries.length === 0) {
    buttonLogger.warn("filterValidEntries", "No valid button entries found after filtering");
    return [];
  }

  return validEntries;
}

/**
 * Step 4: Maps entries to button items
 */
function mapEntriesToItems(
  validEntries: [string, IElementButton][],
  componentStyle?: IStyleComponentItem
): IDynamicButtonItem[] {
  return validEntries
    .map(([key, buttonData]) => createButtonItem(key, buttonData, componentStyle))
    .filter((item): item is IDynamicButtonItem => item !== null);
}

/**
 * Transforms buttons object into array of button items using a 5-step pipeline.
 * Each step is broken into its own function for better readability and maintenance.
 *
 * @param buttonsObj - Valid buttons object to transform
 * @param componentStyle - Component style configuration for button styling (optional)
 * @returns Array of dynamic button items with processed data structure
 */
export function transformButtonsToItems(
  buttonsObj: IDynamicButtonMap,
  componentStyle?: IStyleComponentItem
): IDynamicButtonItem[] {
  try {
    // Step 1: Validate input
    if (!validateButtonsInput(buttonsObj)) return [];

    // Step 2: Extract entries
    const buttonEntries = extractButtonEntries(buttonsObj);
    if (buttonEntries.length === 0) return [];

    // Step 3: Filter valid entries
    const validEntries = filterValidEntries(buttonEntries);
    if (validEntries.length === 0) return [];

    // Step 4: Map to button items
    const buttonItems = mapEntriesToItems(validEntries, componentStyle);

    // Step 5: Return results
    return buttonItems;
  } catch (error) {
    buttonLogger.error("transformButtonsToItems", error);
    return [];
  }
}

//#endregion

//#region Button Item Creation System

/**
 * Builds a button component with error handling and fallback support.
 */
function buildButtonComponent(
  key: string,
  buttonData: IElementButton,
  componentStyle?: IStyleComponentItem
): MessageStructureComponent {
  try {
    return getDynamicButtonProps(key, buttonData, componentStyle);
  } catch {
    return {
      type: MESSAGE_COMPONENT_TYPE.Button,
      label: buttonData?.label || "",
    };
  }
}

/**
 * Builds a button style with error handling and fallback support.
 */
function buildButtonStyle(key: string, componentStyle?: IStyleComponentItem): unknown {
  try {
    return getDynamicButtonStyle(key, componentStyle) || {};
  } catch {
    return {};
  }
}

/**
 * Creates a standardized button item from validated button data.
 *
 * @param key - The original button key from the buttons object
 * @param buttonData - Valid button data to create item from (must be IElementButton)
 * @returns Standardized dynamic button item structure, or fallback item on error
 */
export function createButtonItem(
  key: string,
  buttonData: IElementButton,
  componentStyle?: IStyleComponentItem
): IDynamicButtonItem | null {
  try {
    // 1: Validate key and buttonData before proceeding
    if (!key || !buttonData) return null;

    // 2: Generate ID, component, and style
    const itemId = getUniqId();
    const component = buildButtonComponent(key, buttonData, componentStyle);
    const style = buildButtonStyle(key, componentStyle);

    // 3: Return standardized item structure
    return { key: itemId, component, style };
  } catch {
    return null;
  }
}

//#endregion

//#region Button Component and Style Generation

/**
 * Checks if a specific button style exists in the component style configuration.
 *
 * @param key - The button key to check
 * @param componentStyle - Component style configuration object
 * @returns True if the button style exists, false otherwise
 */
export function hasButtonStyle(key: string, componentStyle: IStyleComponentItem): boolean {
  try {
    const btn = componentStyle.buttons?.[key];
    return !!btn && typeof btn === "object";
  } catch {
    return false;
  }
}

/**
 * Gets the button style configuration for a specific key.
 *
 * @param key - The button key
 * @param componentStyle - Component style configuration object
 * @returns Button style configuration or null if not found
 */
export function getButtonStyleConfig(key: string, componentStyle: IStyleComponentItem): IComponentStyle | null {
  try {
    return hasButtonStyle(key, componentStyle) ? componentStyle.buttons![key] : null;
  } catch {
    return null;
  }
}

/**
 * Determines the button type based on the provided style configuration.
 *
 * @param styleConfig - The component style configuration that may contain a type property.
 * @returns The message component type for the button
 */
function getButtonType(styleConfig: IComponentStyle | null): MESSAGE_COMPONENT_TYPE {
  return (styleConfig?.type as MESSAGE_COMPONENT_TYPE) || MESSAGE_COMPONENT_TYPE.Button;
}

/**
 * Gets button properties for a dynamic button.
 *
 * @param key - The button key
 * @param buttonData - The button data from the buttons object (must be IElementButton)
 * @param componentStyle - Component style configuration object
 * @returns MessageStructureComponent for the dynamic button
 */
export function getDynamicButtonProps(
  key: string,
  buttonData: IElementButton,
  componentStyle: IStyleComponentItem
): MessageStructureComponent | null {
  try {
    if (!buttonData) return null; // Skip if buttonData is null or undefined
    const buttonStyleConfig = getButtonStyleConfig(key, componentStyle);

    return {
      ...buttonData,
      type: getButtonType(buttonStyleConfig),
    };
  } catch (error) {
    buttonLogger.error("getDynamicButtonProps", error);
    return null;
  }
}

/**
 * Gets style for a dynamic button
 *
 * @param key - The button key
 * @param componentStyle - Component style configuration object
 * @returns Style object for the dynamic button with typed CSS properties
 */
export function getDynamicButtonStyle(key: string, componentStyle: IStyleComponentItem): unknown {
  try {
    const buttonStyleConfig = getButtonStyleConfig(key, componentStyle);

    if (buttonStyleConfig) {
      const { textAlign, fontSize, fontWeight, maxLines, color, backgroundColor, margin, padding, background } =
        buttonStyleConfig;

			// Map button style properties to CSS properties
      return {
        textAlign,
        fontSize,
        fontWeight,
        color,
        background: background || backgroundColor,
        maxLines,
        margin,
        padding,
        WebkitLineClamp: maxLines,
        lineClamp: maxLines,
      };
    }

    return {}; // Else, return an empty style object
  } catch (error) {
    buttonLogger.error("getDynamicButtonStyle", error);
    return {};
  }
}

//#endregion
